import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { BarChart3, Grid2X2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { WaveWeek } from './model'
import {
  formatRollupDate,
  formatRollupValue,
  type RollupChartMetric,
  type RollupGroup,
  type RollupHighlight,
  type RollupPlan,
} from './rollup-model'

const labels = { percent: 'Wave %', units: 'Units', cube: 'Cube (ft³)' }
const palette = [
  '#ae1f3e',
  '#397f86',
  '#7760a3',
  '#bc721e',
  '#53723a',
  '#52627e',
]
const planColor = (row: RollupPlan) =>
  palette[Number(row.plan.subclassId) % palette.length]
const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

function describe(row: RollupPlan, week: WaveWeek) {
  const value = row.waves[week.week]
  return `${row.plan.name}, ${week.event}, ${formatRollupDate(week.date)}: ${formatRollupValue(value.percent, 'percent')}, ${formatRollupValue(value.units, 'units')} units, ${formatRollupValue(value.cube, 'cube')} ft³`
}

export interface RollupChartActivation {
  metric: RollupChartMetric
  focus: boolean
}

export function WaveRollupChart({
  group,
  weeks,
  highlight,
  onHighlight,
}: {
  group: RollupGroup
  weeks: WaveWeek[]
  highlight: RollupHighlight | null
  onHighlight: (
    selection: RollupHighlight | null,
    activation?: RollupChartActivation
  ) => void
}) {
  const titleId = useId()
  const readoutId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const [availableWidth, setAvailableWidth] = useState(680)
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0)
        setAvailableWidth(Math.floor(entry.contentRect.width))
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])
  const [view, setView] = useState<'heatmap' | 'bars'>('heatmap')
  const [metric, setMetric] = useState<RollupChartMetric>('percent')
  const [inspected, setInspected] = useState<RollupHighlight | null>(null)
  const row = group.plans.find(item => item.plan.id === inspected?.planId)
  const week = weeks.find(item => item.week === inspected?.week)

  const activate = (row: RollupPlan, week: WaveWeek, focus = false) => {
    setInspected({ planId: row.plan.id, week: week.week })
    onHighlight({ planId: row.plan.id, week: week.week }, { metric, focus })
  }
  const onMarkKey = (
    event: KeyboardEvent<SVGGElement>,
    row: RollupPlan,
    week: WaveWeek
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      activate(row, week, true)
    } else if (event.key === 'Escape') {
      onHighlight(null)
      setInspected(null)
    } else if (
      [
        'ArrowRight',
        'ArrowLeft',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      event.preventDefault()
      const marks = [
        ...event.currentTarget.ownerSVGElement!.querySelectorAll<SVGGElement>(
          '[data-chart-mark]'
        ),
      ]
      const index = marks.indexOf(event.currentTarget)
      const stride = view === 'heatmap' ? weeks.length : group.plans.length
      const offset =
        event.key === 'ArrowRight'
          ? view === 'heatmap'
            ? 1
            : stride
          : event.key === 'ArrowLeft'
            ? view === 'heatmap'
              ? -1
              : -stride
            : event.key === 'ArrowDown'
              ? view === 'heatmap'
                ? stride
                : 1
              : view === 'heatmap'
                ? -stride
                : -1
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? marks.length - 1
            : Math.min(marks.length - 1, Math.max(0, index + offset))
      marks[next]?.focus()
    }
  }
  const markProps = (row: RollupPlan, week: WaveWeek) => ({
    role: 'button',
    tabIndex: 0,
    className: 'rollup-chart-mark',
    'data-chart-mark': `${row.plan.id}:${week.week}`,
    'aria-label': describe(row, week),
    'aria-pressed':
      highlight?.planId === row.plan.id && highlight.week === week.week,
    onFocus: (event: React.FocusEvent<SVGGElement>) => {
      setInspected({ planId: row.plan.id, week: week.week })
      const region = event.currentTarget.closest<HTMLElement>(
        '[data-chart-scroll]'
      )
      if (region) {
        const bounds = event.currentTarget.getBoundingClientRect()
        const left =
          region.getBoundingClientRect().left + (view === 'heatmap' ? 176 : 0)
        if (bounds.left < left) region.scrollLeft += bounds.left - left - 3
      }
    },
    onBlur: () => setInspected(null),
    onPointerEnter: () =>
      setInspected({ planId: row.plan.id, week: week.week }),
    onClick: () => activate(row, week),
    onKeyDown: (event: KeyboardEvent<SVGGElement>) =>
      onMarkKey(event, row, week),
  })
  const faded = (row: RollupPlan, week: WaveWeek) =>
    highlight &&
    (highlight.planId !== row.plan.id ||
      (highlight.week !== undefined && highlight.week !== week.week))

  const left = view === 'heatmap' ? 0 : 56
  const labelWidth = view === 'heatmap' ? 176 : 0
  const width = Math.max(
    320,
    availableWidth - labelWidth,
    left + weeks.length * 92 + 16
  )
  const plotWidth = width - left - 16
  const band = plotWidth / Math.max(1, weeks.length)
  const heatHeight = group.plans.length * 36 + 44
  const heatMax =
    metric === 'percent'
      ? 1
      : Math.max(
          1,
          ...group.plans.flatMap(plan =>
            weeks.map(week => plan.waves[week.week][metric] ?? 0)
          )
        )
  const barMax =
    Math.max(1, ...weeks.map(week => group.waves[week.week][metric] ?? 0)) * 1.1
  const barTop = 24,
    barBottom = 174
  const barY = (value: number) =>
    barBottom - (value / barMax) * (barBottom - barTop)

  return (
    <div
      ref={containerRef}
      className="relative min-w-0 border-b px-4 py-3"
      onPointerLeave={() => setInspected(null)}
      data-testid={`rollup-chart-${group.calendar.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 id={titleId} className="text-sm font-semibold">
            {view === 'heatmap'
              ? 'Plan distribution by wave'
              : 'Volume by wave'}
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label="Chart view"
            className="flex rounded-md border p-0.5"
          >
            <Button
              size="sm"
              variant={view === 'heatmap' ? 'secondary' : 'ghost'}
              aria-pressed={view === 'heatmap'}
              onClick={() => setView('heatmap')}
            >
              <Grid2X2 size={14} /> Heatmap
            </Button>
            <Button
              size="sm"
              variant={view === 'bars' ? 'secondary' : 'ghost'}
              aria-pressed={view === 'bars'}
              onClick={() => {
                setView('bars')
                if (metric === 'percent') setMetric('units')
              }}
            >
              <BarChart3 size={14} /> Bars
            </Button>
          </div>
          <select
            aria-label="Chart metric"
            className="h-8 rounded-md border bg-background px-2 text-xs focus-visible:outline-primary"
            value={metric}
            onChange={event =>
              setMetric(event.target.value as RollupChartMetric)
            }
          >
            {view === 'heatmap' && <option value="percent">Wave %</option>}
            <option value="units">Units</option>
            <option value="cube">Cube (ft³)</option>
          </select>
        </div>
      </div>

      {!weeks.length ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No active waves. Use Show all weeks to inspect the calendar.
        </p>
      ) : (
        <>
          <div
            className="rollup-chart-scroll mt-2 overflow-x-auto"
            data-chart-scroll
            role="region"
            aria-label={`${group.calendar.label} ${view} chart`}
          >
            <div className="flex" style={{ width: width + labelWidth }}>
              {view === 'heatmap' && (
                <div
                  className="rollup-heatmap-labels"
                  style={{ width: labelWidth, height: heatHeight }}
                  aria-hidden="true"
                >
                  <div className="h-[39px]" />
                  {group.plans.map(plan => (
                    <div
                      key={plan.plan.id}
                      className="flex h-9 items-center pr-3"
                      title={plan.plan.name}
                    >
                      <span className="truncate">{plan.plan.name}</span>
                    </div>
                  ))}
                </div>
              )}
              <svg
                viewBox={`0 0 ${width} ${view === 'heatmap' ? heatHeight : 220}`}
                style={{ width, height: view === 'heatmap' ? heatHeight : 220 }}
                className="block shrink-0"
                role="group"
                aria-labelledby={titleId}
                aria-describedby={inspected ? readoutId : undefined}
              >
                {view === 'heatmap' ? (
                  <>
                    {weeks.map((week, index) => (
                      <g key={week.week} aria-hidden="true">
                        <text
                          x={left + band * (index + 0.5)}
                          y={15}
                          textAnchor="middle"
                          fontSize={11}
                          fill="var(--color-foreground)"
                          fontWeight={600}
                        >
                          {week.event}
                        </text>
                        <text
                          x={left + band * (index + 0.5)}
                          y={31}
                          textAnchor="middle"
                          fontSize={10}
                          fill="var(--color-muted-foreground)"
                        >
                          {formatRollupDate(week.date)}
                        </text>
                      </g>
                    ))}
                    {group.plans.map((row, rowIndex) => (
                      <g key={row.plan.id}>
                        {weeks.map((week, index) => {
                          const value = row.waves[week.week][metric]
                          const intensity = Math.min(
                            1,
                            Math.max(0, (value ?? 0) / heatMax)
                          )
                          const selected =
                            highlight?.planId === row.plan.id &&
                            highlight.week === week.week
                          return (
                            <g
                              key={week.week}
                              {...markProps(row, week)}
                              opacity={faded(row, week) ? 0.4 : 1}
                            >
                              <title>{describe(row, week)}</title>
                              <rect
                                x={left + index * band + 3}
                                y={rowIndex * 36 + 40}
                                width={band - 6}
                                height={30}
                                rx={3}
                                fill={
                                  value
                                    ? `hsl(350 58% ${97 - intensity * 62}%)`
                                    : '#f3f4f6'
                                }
                                stroke={
                                  selected
                                    ? 'var(--color-primary)'
                                    : 'var(--color-border)'
                                }
                                strokeWidth={selected ? 2 : 0.5}
                              />
                              <text
                                x={left + band * (index + 0.5)}
                                y={rowIndex * 36 + 60}
                                textAnchor="middle"
                                fontSize={12}
                                fontWeight={600}
                                fill={intensity > 0.55 ? '#fff' : '#252525'}
                                aria-hidden="true"
                                pointerEvents="none"
                              >
                                {formatRollupValue(value, metric)}
                              </text>
                            </g>
                          )
                        })}
                      </g>
                    ))}
                  </>
                ) : (
                  <>
                    <text
                      x={left}
                      y={14}
                      fontSize={11}
                      fill="var(--color-muted-foreground)"
                      aria-hidden="true"
                    >
                      {labels[metric]}
                    </text>
                    {[0, 1, 2, 3, 4].map(tick => {
                      const value = (barMax * tick) / 4
                      return (
                        <g key={tick} aria-hidden="true">
                          <line
                            x1={left}
                            x2={width - 16}
                            y1={barY(value)}
                            y2={barY(value)}
                            stroke="var(--color-border)"
                            strokeDasharray="3 4"
                          />
                          <text
                            x={left - 10}
                            y={barY(value) + 4}
                            textAnchor="end"
                            fontSize={10}
                            fill="var(--color-muted-foreground)"
                          >
                            {compact.format(value)}
                          </text>
                        </g>
                      )
                    })}
                    {weeks.map((week, index) => {
                      let stacked = 0
                      const total = group.waves[week.week][metric]
                      return (
                        <g key={week.week}>
                          {total === null ? (
                            <text
                              x={left + band * (index + 0.5)}
                              y={barBottom - 8}
                              textAnchor="middle"
                              fill="var(--color-muted-foreground)"
                            >
                              —
                            </text>
                          ) : (
                            group.plans.map(row => {
                              const value = row.waves[week.week][metric] ?? 0
                              stacked += value
                              const selected =
                                highlight?.planId === row.plan.id &&
                                highlight.week === week.week
                              return (
                                <g
                                  key={row.plan.id}
                                  {...markProps(row, week)}
                                  opacity={faded(row, week) ? 0.3 : 1}
                                >
                                  <title>{describe(row, week)}</title>
                                  <rect
                                    x={left + index * band + band * 0.2}
                                    y={barY(stacked)}
                                    width={band * 0.6}
                                    height={
                                      (value / barMax) * (barBottom - barTop)
                                    }
                                    fill={planColor(row)}
                                    stroke={
                                      selected
                                        ? 'var(--color-foreground)'
                                        : 'var(--color-card)'
                                    }
                                    strokeWidth={selected ? 2 : 1}
                                  />
                                </g>
                              )
                            })
                          )}
                          <text
                            x={left + band * (index + 0.5)}
                            y={barBottom + 22}
                            textAnchor="middle"
                            fontSize={11}
                            fontWeight={600}
                            fill="var(--color-foreground)"
                            aria-hidden="true"
                          >
                            {week.event}
                          </text>
                          <text
                            x={left + band * (index + 0.5)}
                            y={barBottom + 39}
                            textAnchor="middle"
                            fontSize={10}
                            fill="var(--color-muted-foreground)"
                            aria-hidden="true"
                          >
                            {formatRollupDate(week.date)}
                          </text>
                        </g>
                      )
                    })}
                  </>
                )}
              </svg>
            </div>
          </div>
          {view === 'heatmap' && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              {labels[metric]} · darker = higher · 0 to{' '}
              {formatRollupValue(heatMax, metric)}
            </p>
          )}
          <p className="mt-2 text-[11px] text-muted-foreground sm:hidden">
            Scroll horizontally to explore all waves.
          </p>
          {view === 'bars' && (
            <div
              className="mt-1 flex flex-wrap gap-x-3 gap-y-1"
              role="group"
              aria-label="Highlight a plan"
            >
              {group.plans.map(row => (
                <button
                  key={row.plan.id}
                  type="button"
                  aria-pressed={highlight?.planId === row.plan.id}
                  className="inline-flex items-center gap-1.5 rounded px-1.5 py-1 text-xs hover:bg-muted focus-visible:outline-primary"
                  onClick={() =>
                    onHighlight(
                      highlight?.planId === row.plan.id &&
                        highlight.week === undefined
                        ? null
                        : { planId: row.plan.id }
                    )
                  }
                >
                  <span
                    className="h-2.5 w-2.5 rounded-sm"
                    style={{ background: planColor(row) }}
                  />
                  {row.plan.name}
                </button>
              ))}
            </div>
          )}
          {row && week && inspected && (
            <div
              id={readoutId}
              className="rollup-chart-readout"
              role="tooltip"
              data-testid="rollup-chart-readout"
            >
              <div>
                <p className="font-semibold">{row.plan.name}</p>
                <p className="mt-1 text-muted-foreground">
                  {week.event} · {formatRollupDate(week.date)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Wave %</p>
                <p className="mt-1 font-medium">
                  {formatRollupValue(row.waves[week.week].percent, 'percent')}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Units</p>
                <p className="mt-1 font-medium">
                  {formatRollupValue(row.waves[week.week].units, 'units')}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Cube (ft³)</p>
                <p className="mt-1 font-medium">
                  {formatRollupValue(row.waves[week.week].cube, 'cube')}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
